import {
  BadgeCheck,
  Building2,
  CalendarClock,
  Car,
  CloudSun,
  CreditCard,
  FileStack,
  FolderKanban,
  Hotel,
  LayoutDashboard,
  Library,
  Megaphone,
  Monitor,
  MonitorSmartphone,
  PlaySquare,
  School,
  ShieldCheck,
  Store,
  TabletSmartphone,
  UsersRound,
  Utensils,
} from "lucide-react";
import type { ElementType } from "react";

export type StatusTone = "available" | "progress" | "soon";

export type MarketingItem = {
  title: string;
  body: string;
  status?: string;
  tone?: StatusTone;
  icon?: ElementType;
};

export const confirmedFeatures: MarketingItem[] = [
  {
    title: "Screen management",
    body: "Track paired, pending, inactive, live, and offline screens with workspace-aware access controls.",
    status: "Available",
    tone: "available",
    icon: Monitor,
  },
  {
    title: "Android and device pairing",
    body: "Pair display players with short codes, recover known devices, and keep runtime keys isolated per screen.",
    status: "Available",
    tone: "available",
    icon: TabletSmartphone,
  },
  {
    title: "Virtual screen runtime",
    body: "Launch browser-based virtual screens, preview screen content, and stream updates through the runtime.",
    status: "Available",
    tone: "available",
    icon: MonitorSmartphone,
  },
  {
    title: "Media library",
    body: "Upload images and videos, organize folders, track usage, move assets, and scope media by workspace.",
    status: "Available",
    tone: "available",
    icon: Library,
  },
  {
    title: "Playlists",
    body: "Build ordered playlists with media duration controls, orientation support, duplication, and screen assignment.",
    status: "Available",
    tone: "available",
    icon: PlaySquare,
  },
  {
    title: "Channels and layouts",
    body: "Create multi-zone channels, choose landscape or portrait layouts, publish snapshots, and discard drafts.",
    status: "Available",
    tone: "available",
    icon: LayoutDashboard,
  },
  {
    title: "Scheduling",
    body: "Schedule channel zone items by date, weekday, time windows, priority, and fullscreen overrides.",
    status: "Available",
    tone: "available",
    icon: CalendarClock,
  },
  {
    title: "Workspaces and roles",
    body: "Separate teams into spaces, invite users, assign organization or workspace roles, and manage memberships.",
    status: "Available",
    tone: "available",
    icon: UsersRound,
  },
  {
    title: "Audit logs",
    body: "Review and export activity history across screen, playlist, channel, media, user, and billing actions.",
    status: "Available",
    tone: "available",
    icon: ShieldCheck,
  },
  {
    title: "Licensing and billing",
    body: "Use per-screen quotas, trial or paid licenses, invoices, Stripe Checkout, and billing portal sessions.",
    status: "Available",
    tone: "available",
    icon: CreditCard,
  },
  {
    title: "Taxi fleet signage",
    body: "Manage taxi vehicles, drivers, trip logs, fleet dashboards, journey progress screens, and taxi display runtime data.",
    status: "Available",
    tone: "available",
    icon: Car,
  },
  {
    title: "App and widget runtime",
    body: "The app framework supports saved web app displays, weather layouts, and specialized runtime dashboards.",
    status: "Designed for",
    tone: "progress",
    icon: CloudSun,
  },
  {
    title: "Smart Ads",
    body: "Designed for in-vehicle campaigns, geo-fences, audience rules, and analytics; the current admin surface is an in-progress product area.",
    status: "In progress",
    tone: "progress",
    icon: Megaphone,
  },
  {
    title: "Screen groups and notifications",
    body: "Data models are in place for grouped screens and recipient-based online or offline notifications.",
    status: "Roadmap",
    tone: "soon",
    icon: FolderKanban,
  },
];

export const roadmapFeatures: MarketingItem[] = [
  {
    title: "Meeting room integrations",
    body: "Google Calendar, Outlook Calendar, Microsoft Teams Rooms, and Zoom Rooms are listed in the app catalog as planned integrations.",
  },
  {
    title: "Expanded app library",
    body: "RSS, social, business dashboards, menu boards, transport feeds, and review widgets are planned app catalog additions.",
  },
  {
    title: "Production Smart Ads data flows",
    body: "Smart Ads campaign UI exists, while production data connections, privacy controls, and reporting flows should be completed before presenting it as fully available.",
  },
  {
    title: "Advanced alerting",
    body: "Screen group and notification models are designed for offline, online, and license threshold workflows.",
  },
];

export const useCases: MarketingItem[] = [
  {
    title: "Corporate offices",
    body: "Announcements, dashboards, safety messages, and employee communications.",
    icon: Building2,
  },
  {
    title: "Meeting room signage",
    body: "Room schedules and wayfinding today, deeper calendar integrations on the roadmap.",
    icon: CalendarClock,
  },
  {
    title: "Hotels and hospitality",
    body: "Lobby notices, restaurant menus, event schedules, and guest information.",
    icon: Hotel,
  },
  {
    title: "Schools and universities",
    body: "Campus updates, alerts, event boards, and department screen networks.",
    icon: School,
  },
  {
    title: "Retail stores",
    body: "Campaigns, product highlights, promotions, and store operations screens.",
    icon: Store,
  },
  {
    title: "Restaurants and cafes",
    body: "Digital menu boards, specials, pickup information, and branded content loops.",
    icon: Utensils,
  },
  {
    title: "Clinics and waiting areas",
    body: "Queue-friendly information, health guidance, and waiting room media.",
    icon: BadgeCheck,
  },
  {
    title: "Event venues and lobbies",
    body: "Directional signage, sponsor loops, welcome screens, and schedule updates.",
    icon: FileStack,
  },
  {
    title: "Taxi and fleet operators",
    body: "Fleet dashboards, trip history, journey progress, driver assignments, and in-vehicle screens.",
    icon: Car,
  },
  {
    title: "Smart advertising networks",
    body: "Targeted in-vehicle campaigns with geo-fences, audience rules, playlists, and analytics workflows.",
    icon: Megaphone,
  },
];

export const howItWorks = [
  "Upload or organize images, videos, and folders in the media library.",
  "Build playlists or multi-zone channels for the right screen format.",
  "Pair Android display players or launch virtual screens for previews.",
  "Schedule content, publish channel snapshots, and assign sources to screens.",
  "Monitor screen status, last-seen activity, billing quota, and audit history centrally.",
];

export const integrations: MarketingItem[] = [
  {
    title: "Stripe",
    body: "Billing, invoices, subscription checkout, customer portal, and per-screen pricing are implemented.",
    status: "Available",
    tone: "available",
    icon: CreditCard,
  },
  {
    title: "Weather data",
    body: "Weather display runtime and location search are present; the catalog still labels this area as ready soon or planned.",
    status: "In progress",
    tone: "progress",
    icon: CloudSun,
  },
  {
    title: "Taxi data sources",
    body: "Taxi ingestion and display code supports le.taxi, Webfleet, iCabbi, and manual fleet data providers.",
    status: "Available",
    tone: "available",
    icon: Car,
  },
  {
    title: "Smart Ads",
    body: "Campaigns, geo-fences, audience rules, and analytics are represented in the admin UI as an in-progress product area.",
    status: "In progress",
    tone: "progress",
    icon: Megaphone,
  },
  {
    title: "Google Calendar",
    body: "Listed in the app catalog as planned for meeting room and event signage workflows.",
    status: "Coming soon",
    tone: "soon",
    icon: CalendarClock,
  },
  {
    title: "Outlook Calendar",
    body: "Listed in the app catalog as planned for shared display schedules.",
    status: "Coming soon",
    tone: "soon",
    icon: CalendarClock,
  },
  {
    title: "Zoom Rooms",
    body: "Listed in the app catalog as planned for meeting room display management.",
    status: "Coming soon",
    tone: "soon",
    icon: MonitorSmartphone,
  },
];

export const faqs = [
  {
    question: "Can I manage multiple screens?",
    answer:
      "Yes. PulsePanels supports multiple screens per organization and workspace, with status, orientation, assigned content, and quota tracking.",
  },
  {
    question: "Do I need an Android device?",
    answer:
      "Android player pairing is supported, and virtual screens are also available for browser-based preview and runtime use.",
  },
  {
    question: "Can I schedule content?",
    answer:
      "Yes. Channel zone items support date ranges, weekday schedules, time windows, priority, and fullscreen playback rules.",
  },
  {
    question: "Can I use PulsePanels for meeting rooms?",
    answer:
      "Yes for screen content, room signage, and schedules you build in the platform. Direct Google, Outlook, Teams Rooms, and Zoom Rooms integrations are roadmap items.",
  },
  {
    question: "Can I preview content before publishing?",
    answer:
      "Yes. Virtual screen previews and published channel snapshots help teams review content before it reaches assigned screens.",
  },
  {
    question: "Can I manage users and permissions?",
    answer:
      "Yes. The platform includes organization users, workspace members, invitations, roles, feature permissions, and audit logs.",
  },
  {
    question: "What happens if a screen goes offline?",
    answer:
      "Screens report last-seen activity through heartbeat and socket events, and the dashboard labels paired screens as live or offline based on recent activity.",
  },
  {
    question: "Can I pay monthly or annually?",
    answer:
      "Yes. Billing is designed around $10 per screen monthly or $100 per screen annually, with Stripe-powered checkout and invoices.",
  },
  {
    question: "Can PulsePanels support taxi and fleet signage?",
    answer:
      "Yes. The platform includes taxi fleet pages, vehicles, drivers, trip logs, runtime dashboards, journey progress displays, and taxi display API endpoints.",
  },
  {
    question: "Are Smart Ads available?",
    answer:
      "Smart Ads is an in-progress product area. The admin portal includes campaign, geo-fence, audience-rule, and analytics surfaces, while production data flows should be completed before treating it as fully live.",
  },
];
